import React, { useState, useRef, useEffect, useCallback } from 'react'
import type { Field } from 'lib/types'
import FieldWrapper from './FieldWrapper'
import styles from './styles'

const SearchableSelect = ({ input, options, placeholderText, disabled, autoFocus }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [displayValue, setDisplayValue] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const containerRef = useRef(null)
  const inputRef = useRef(null)
  const optionRefs = useRef([])

  const filteredOptions = options.filter((option) => {
    const label = option.label || option.toString()
    return label.toLowerCase().includes(searchTerm.toLowerCase())
  })

  useEffect(() => {
    setHighlightedIndex(-1)
  }, [filteredOptions.length, searchTerm])

  useEffect(() => {
    if (highlightedIndex >= 0 && optionRefs.current[highlightedIndex]) {
      optionRefs.current[highlightedIndex].scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      })
    }
  }, [highlightedIndex])

  useEffect(() => {
    if (input.value) {
      const selectedOption = options.find((opt) => opt.value === input.value)
      setDisplayValue(selectedOption ? selectedOption.label : input.value)
    } else {
      setDisplayValue('')
    }
  }, [input.value, options])

  const handleClickOutside = useCallback((event) => {
    if (containerRef.current && !containerRef.current.contains(event.target)) {
      setIsOpen(false)
      setSearchTerm('')
      setHighlightedIndex(-1)
    }
  }, [])

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [handleClickOutside])

  const handleInputClick = useCallback(() => {
    if (!disabled) {
      setIsOpen(!isOpen)
      setSearchTerm('')
      setHighlightedIndex(-1)
    }
  }, [disabled, isOpen])

  const handleSearchChange = useCallback(
    (e) => {
      const value = e.target.value
      setSearchTerm(value)
      setIsOpen(true)

      if (value && filteredOptions.length > 0) {
        setHighlightedIndex(0)
      } else {
        setHighlightedIndex(-1)
      }
    },
    [filteredOptions.length],
  )

  const handleOptionSelect = useCallback(
    (option) => {
      input.onChange(option.value)
      setDisplayValue(option.label)
      setIsOpen(false)
      setSearchTerm('')
      setHighlightedIndex(-1)
      inputRef.current?.focus()
    },
    [input],
  )

  const handleKeyDown = useCallback(
    (e) => {
      if (disabled) return

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          if (!isOpen) {
            setIsOpen(true)
            setSearchTerm('')
            setHighlightedIndex(0)
          } else {
            setHighlightedIndex((prev) => (prev < filteredOptions.length - 1 ? prev + 1 : prev))
          }
          break

        case 'ArrowUp':
          e.preventDefault()
          if (isOpen) {
            setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev))
          }
          break

        case 'Enter':
          e.preventDefault()
          if (isOpen) {
            if (filteredOptions.length === 1) {
              handleOptionSelect(filteredOptions[0])
            } else if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
              handleOptionSelect(filteredOptions[highlightedIndex])
            } else if (filteredOptions.length > 0) {
              handleOptionSelect(filteredOptions[0])
            }
          } else {
            setIsOpen(true)
            setSearchTerm('')
            setHighlightedIndex(-1)
          }
          break

        case 'Escape':
          e.preventDefault()
          if (isOpen) {
            setIsOpen(false)
            setSearchTerm('')
            setHighlightedIndex(-1)
            const selectedOption = options.find((opt) => opt.value === input.value)
            setDisplayValue(selectedOption ? selectedOption.label : input.value || '')
          }
          break
      }
    },
    [disabled, isOpen, highlightedIndex, filteredOptions, handleOptionSelect, options, input.value],
  )

  const handleOptionMouseEnter = useCallback((index) => {
    setHighlightedIndex(index)
  }, [])

  const handleOptionMouseLeave = useCallback(() => {
    setHighlightedIndex(-1)
  }, [])

  const selectClassName = disabled ? `${styles.select} ${styles.selectDisabled}` : styles.select

  return (
    <div className={styles.searchableSelectContainer} ref={containerRef}>
      <div className={styles.searchableSelectInput} onClick={handleInputClick}>
        <input
          ref={inputRef}
          type="text"
          value={isOpen ? searchTerm : displayValue}
          onChange={handleSearchChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholderText || 'Type to search or select an option'}
          className={`${styles.searchableSelectInputField} ${selectClassName}`}
          disabled={disabled}
          autoFocus={autoFocus}
          readOnly={false}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-autocomplete="list"
          aria-activedescendant={isOpen && highlightedIndex >= 0 ? `option-${input.name}-${highlightedIndex}` : undefined}
        />
      </div>

      {isOpen && !disabled && (
        <div className={styles.searchableSelectDropdown} role="listbox" aria-label="Options">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <div
                key={option.value}
                ref={(el) => (optionRefs.current[index] = el)}
                id={`option-${input.name}-${index}`}
                className={`${styles.searchableSelectOption} ${option.value === input.value ? styles.searchableSelectOptionSelected : ''} ${
                  index === highlightedIndex ? styles.searchableSelectOptionHighlighted : ''
                }`}
                onClick={() => handleOptionSelect(option)}
                onMouseEnter={() => handleOptionMouseEnter(index)}
                onMouseLeave={handleOptionMouseLeave}
                role="option"
                aria-selected={option.value === input.value}
              >
                {option.label}
              </div>
            ))
          ) : (
            <div className={styles.searchableSelectNoOptions}>No options found</div>
          )}
        </div>
      )}
    </div>
  )
}

const markup = (field: Field) => {
  const { autoFocus, input, options: opts, placeholderText, disabled } = field

  let transformedOptions = []

  if (Array.isArray(opts)) {
    if (opts.length > 0 && typeof opts[0] === 'object' && opts[0].hasOwnProperty('value')) {
      transformedOptions = opts.map((option) => ({
        value: option.value,
        label: option.label || option.value,
      }))
    } else {
      transformedOptions = opts.map((val) => ({
        value: val,
        label: val,
      }))
    }
  } else if (opts && typeof opts === 'object') {
    transformedOptions = Object.keys(opts).map((key) => ({
      value: key,
      label: opts[key],
    }))
  }

  if (field.newValue) {
    input.value = field.newValue
  }

  return <SearchableSelect input={input} options={transformedOptions} placeholderText={placeholderText} disabled={disabled} autoFocus={autoFocus} />
}

const renderSearchableSelectField = (field: Field) => {
  const {
    button,
    children,
    disabled,
    input,
    label,
    meta,
    meta: { asyncValidating },
    placeholder,
    required,
    type,
  } = field

  const props = {
    asyncValidating,
    button,
    disabled,
    innerButton: children,
    label: label || null,
    meta,
    name: input.name,
    placeholder,
    required,
    type,
  }

  return <FieldWrapper {...props}>{markup(field)}</FieldWrapper>
}

export default renderSearchableSelectField
